import React, { useRef, useEffect, useState, useCallback } from "react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@bindery/ui/components/ui/button";
import { useInputType } from "@bindery/ui/hooks/use-input-type";
import type { Logger } from "@bindery/logging";
import type {
  VocabularyWord,
  WordStatus,
} from "@/ear-reader/types/vocabulary-exercise";

// Types

/**
 * Re-export VocabularyWord as Word for backward compatibility.
 * New code should prefer importing VocabularyWord directly from
 * @/lib/types/vocabulary-exercise.
 */
export type Word = VocabularyWord;

export interface SwipeableItemProps {
  word: Word;
  onStatusChange: (id: number, newStatus: WordStatus) => void;
  isFocused: boolean;
  onFocus: () => void;
  index: number;
  ref?: React.Ref<HTMLDivElement> | null;
  /** Whether the aide drawer is open (suppresses keyboard handling) */
  isDrawerOpen?: boolean;
  /** Logger instance for structured telemetry (required for event tracking) */
  log: Logger;
}

/** Minimum drag distance (in pixels) to trigger a swipe action */
const SWIPE_THRESHOLD = 100;

// Background style lookup

/** Background style classes by word status. Defined outside component for reuse. */
const BACKGROUND_STYLE_BY_STATUS: Record<WordStatus, string> = {
  recognized:
    "bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700",
  unrecognized:
    "bg-gray-200 border-red-200 dark:bg-gray-700 dark:border-red-700",
  unreviewed: "bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-600",
};

/** Get background style classes for a word status */
function getBackgroundStyle(status: WordStatus): string {
  return BACKGROUND_STYLE_BY_STATUS[status];
}

// SwipeableItem — swipeable vocabulary word card

/**
 * Implements pointer-event swipe detection and keyboard controls for a single
 * vocabulary word. Swipe right to recognize, left to mark as unrecognized.
 *
 * Implementation notes:
 * - Uses a callbacksRef pattern to keep pointer event handlers stable while
 *   always calling the latest callback versions. The pointer event effect
 *   only re-runs when the DOM element changes, not when callbacks change.
 *   This is valuable because SwipeableVocabularyList is wrapped in memo()
 *   and passes these as children — stable event listeners prevent unnecessary
 *   DOM re-registration.
 * - Background styles are computed via a lookup table outside the component
 *   to avoid recreating the function on each render.
 */
export const SwipeableItem: React.FC<SwipeableItemProps> = ({
  word,
  onStatusChange,
  isFocused,
  onFocus,
  index,
  ref,
  isDrawerOpen = false,
  log,
}) => {
  const dragDistance = useRef(0);
  const startX = useRef(0);
  const pointerStartTime = useRef(0);
  const itemRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  const inputType = useInputType();

  // Keep mutable refs for values used inside pointer event handlers
  // so the pointer event effect only re-registers when the container element changes.
  // This ensures stable event listeners while always calling the latest callbacks.
  const callbacksRef = useRef({
    onStatusChange,
    log,
    wordId: word.id,
    wordSlug: word.slug,
  });
  useEffect(() => {
    callbacksRef.current = {
      onStatusChange,
      log,
      wordId: word.id,
      wordSlug: word.slug,
    };
  }, [onStatusChange, log, word.id, word.slug]);

  // --- Focus management ---

  const resolvedRef = (ref as React.RefObject<HTMLDivElement>) ?? itemRef;

  useEffect(() => {
    if (isFocused && resolvedRef.current) {
      resolvedRef.current.focus();
    }
  }, [isFocused, resolvedRef]);

  // --- Status change with logging ---

  const handleStatusChangeWithLog = useCallback(
    (
      newStatus: WordStatus,
      inputMethod: "swipe" | "keyboard" | "click",
      swipeDirection?: "left" | "right",
    ) => {
      log.event("reading.vocabulary.word.status_change", {
        word_slug: word.slug,
        status: newStatus,
        input_type: inputMethod,
        ...(swipeDirection && { swipe_direction: swipeDirection }),
      });
      onStatusChange(word.id, newStatus);
    },
    [word.id, word.slug, onStatusChange, log],
  );

  // --- Swipe / pointer event handling ---
  // Uses callbacksRef so the effect only re-runs when the DOM element changes,
  // not when callbacks or word identity change. The pointerdown/pointerup
  // listeners read the latest values from callbacksRef.current at event time.

  useEffect(() => {
    const container = resolvedRef.current;
    if (!container) return;

    const handlePointerDown = (e: PointerEvent) => {
      startX.current = e.clientX;
      pointerStartTime.current = Date.now();
      dragDistance.current = 0;
    };

    const handlePointerUp = (e: PointerEvent) => {
      dragDistance.current = e.clientX - startX.current;
      const duration = Date.now() - pointerStartTime.current;
      const {
        onStatusChange: cb,
        log: logger,
        wordId,
        wordSlug,
      } = callbacksRef.current;

      if (dragDistance.current > SWIPE_THRESHOLD) {
        logger.event("reading.vocabulary.word.swipe", {
          word_slug: wordSlug,
          direction: "right",
          duration_ms: duration,
        });
        cb(wordId, "recognized");
      } else if (dragDistance.current < -SWIPE_THRESHOLD) {
        logger.event("reading.vocabulary.word.swipe", {
          word_slug: wordSlug,
          direction: "left",
          duration_ms: duration,
        });
        cb(wordId, "unrecognized");
      }

      dragDistance.current = 0;
    };

    container.addEventListener("pointerdown", handlePointerDown);
    container.addEventListener("pointerup", handlePointerUp);

    return () => {
      container.removeEventListener("pointerdown", handlePointerDown);
      container.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resolvedRef]);

  // --- Keyboard: arrow keys to mark status ---

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isDrawerOpen) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleStatusChangeWithLog("recognized", "keyboard");
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleStatusChangeWithLog("unrecognized", "keyboard");
      }
    },
    [isDrawerOpen, handleStatusChangeWithLog],
  );

  // --- Click handlers extracted for stability ---

  const handleClickUnrecognized = useCallback(
    () => handleStatusChangeWithLog("unrecognized", "click"),
    [handleStatusChangeWithLog],
  );

  const handleClickRecognized = useCallback(
    () => handleStatusChangeWithLog("recognized", "click"),
    [handleStatusChangeWithLog],
  );

  const handleClickReset = useCallback(
    () => handleStatusChangeWithLog("unreviewed", "click"),
    [handleStatusChangeWithLog],
  );

  const handleIndicatorKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        action();
      }
    },
    [],
  );

  // --- Render ---

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- a focusable swipe row inside a list; arrow keys act on the focused row
    <div
      className={`relative mb-4 w-full overflow-hidden transition-all duration-150 ${
        isFocused
          ? "rounded-lg ring-2 ring-indigo-400 dark:ring-indigo-500"
          : ""
      }`}
      onClick={onFocus}
      ref={resolvedRef}
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a focusable swipe row inside a list; arrow keys act on the focused row
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a focusable swipe row inside a list; arrow keys act on the focused row
      role="listitem"
      aria-label={`Word: ${word.text}, status: ${word.status ?? "unreviewed"}. Use right arrow to mark as recognized, left arrow to mark as not recognized.`}
      data-index={index}
      data-navbar-ignore
    >
      {/* Background success indicator */}
      <div
        className="absolute inset-0 left-0 flex w-1/2 items-center rounded-lg bg-green-500 px-6 dark:bg-green-600"
        aria-hidden="true"
      >
        <CheckIcon className="h-8 w-8 text-white" />
      </div>

      {/* Background failure indicator */}
      <div
        className="absolute inset-0 left-1/2 flex w-1/2 items-center justify-end rounded-lg bg-gray-500 px-6 dark:bg-gray-600"
        aria-hidden="true"
      >
        <XMarkIcon className="h-8 w-8 text-white" />
      </div>

      <div
        className={`relative rounded-lg border-2 p-6 shadow-sm ${getBackgroundStyle(word.status ?? "unreviewed")} flex items-center justify-between transition-all duration-200`}
      >
        {/* Left indicator icon (touch) */}
        {inputType === "touch" && (
          <button
            type="button"
            className="absolute left-6 appearance-none border-none bg-transparent p-0"
            onClick={handleClickUnrecognized}
            aria-label="Mark as not recognized"
          >
            <XMarkIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
          </button>
        )}
        {/* Action buttons for mouse users */}
        {inputType === "mouse" && isHovering && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="absolute left-2 border-red-300 bg-white text-red-600 hover:bg-red-50 dark:border-red-700 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/30"
              onClick={handleClickUnrecognized}
              aria-label="Mark as not recognized"
            >
              <XMarkIcon className="mr-1 h-4 w-4" /> Don&apos;t Know
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="absolute right-2 border-green-300 bg-white text-green-600 hover:bg-green-50 dark:border-green-700 dark:bg-gray-800 dark:text-green-400 dark:hover:bg-green-900/30"
              onClick={handleClickRecognized}
              aria-label="Mark as recognized"
            >
              Know <CheckIcon className="ml-1 h-4 w-4" />
            </Button>
          </>
        )}
        {/* Main word display */}
        <div className="w-full text-center">
          <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
            {word.text}
          </h3>
        </div>
        {/* Right indicator icon (touch) */}
        {inputType === "touch" && (
          <button
            type="button"
            className="absolute right-6 appearance-none border-none bg-transparent p-0"
            onClick={handleClickRecognized}
            aria-label="Mark as recognized"
          >
            <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
          </button>
        )}
        {/* Reset control if word has been reviewed */}
        {word.status !== "unreviewed" && (
          <button
            type="button"
            className="absolute top-3 right-3 cursor-pointer rounded-full bg-white p-1 shadow-sm dark:bg-gray-700"
            onClick={handleClickReset}
            onKeyDown={(e) => handleIndicatorKeyDown(e, handleClickReset)}
            aria-label="Reset status"
          >
            <XMarkIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
};
